import jsPDF from 'jspdf';

interface GameTranscriptData {
  gameType: string;
  gameName: string;
  difficulty: string;
  finalScore: number;
  trustLevel: string;
  messages: Array<{
    type: 'user' | 'system';
    content: string;
    isFeedback?: boolean;
  }>;
}

export class PDFService {
  private static readonly PAGE_MARGIN = 20;
  private static readonly CONTENT_WIDTH_MARGIN = 40;

  private static getPageDimensions(doc: jsPDF): { width: number; height: number } {
    return {
      width: doc.internal.pageSize.width,
      height: doc.internal.pageSize.height,
    };
  }

  private static addWrappedText(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    fontSize: number = 11
  ): number {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return lines.length * (fontSize * 0.5);
  }

  private static addCenteredText(
    doc: jsPDF,
    text: string,
    y: number,
    size: number = 16
  ): void {
    doc.setFontSize(size);
    const textWidth = doc.getTextWidth(text);
    const pageWidth = doc.internal.pageSize.width;
    const x = (pageWidth - textWidth) / 2;
    doc.text(text, x, y);
  }

  private static drawDecorations(doc: jsPDF): void {
    const { width, height } = this.getPageDimensions(doc);
    const margin = this.PAGE_MARGIN;
    const cornerSize = 10;

    // Draw borders
    doc.setDrawColor(128, 128, 128);
    doc.setLineWidth(0.5);
    doc.rect(margin, margin, width - 2 * margin, height - 2 * margin);
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(margin + 3, margin + 3, width - 2 * (margin + 3), height - 2 * (margin + 3));

    // Draw corners
    doc.setLineWidth(0.5);
    doc.setDrawColor(100, 100, 100);

    // Top left
    doc.line(margin, margin + cornerSize, margin, margin);
    doc.line(margin, margin, margin + cornerSize, margin);

    // Top right
    doc.line(width - margin - cornerSize, margin, width - margin, margin);
    doc.line(width - margin, margin, width - margin, margin + cornerSize);

    // Bottom left
    doc.line(margin, height - margin - cornerSize, margin, height - margin);
    doc.line(margin, height - margin, margin + cornerSize, height - margin);

    // Bottom right
    doc.line(width - margin - cornerSize, height - margin, width - margin, height - margin);
    doc.line(width - margin, height - margin - cornerSize, width - margin, height - margin);
  }

  private static formatGameTitle(gameName: string): string {
    return `${gameName} - Spiritual Journey Record`;
  }

  public static generateTranscript(data: GameTranscriptData): void {
    try {
      // Initialize jsPDF with required options
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true,
        floatPrecision: 16
      });

      // Wait for fonts to be loaded
      doc.setFont("helvetica");

      const { width, height } = this.getPageDimensions(doc);
      const margin = this.PAGE_MARGIN;
      let yPosition = margin + 15;

      // Set document properties
      doc.setProperties({
        title: `${data.gameName} - Spiritual Journey Record`,
        subject: 'Game Transcript',
        author: 'Spirit Guide',
        keywords: 'spiritual journey, game transcript',
        creator: 'Afterlife Guidance System'
      });

      // Add decorations
      this.drawDecorations(doc);

      // Add title with shadow effect
      const title = this.formatGameTitle(data.gameName);
      doc.setFillColor(51, 51, 51);
      this.addCenteredText(doc, title, yPosition + 0.2, 24);
      doc.setFillColor(0, 0, 0);
      this.addCenteredText(doc, title, yPosition, 24);
      yPosition += 15;

      // Add decorative line
      doc.setDrawColor(128, 128, 128);
      doc.setLineWidth(0.5);
      doc.line(margin + this.CONTENT_WIDTH_MARGIN, yPosition, width - margin - this.CONTENT_WIDTH_MARGIN, yPosition);
      yPosition += 10;

      // Add game details box
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(margin + 20, yPosition, width - 2 * (margin + 20), 30, 3, 3, 'FD');

      // Add game details
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(51, 51, 51);

      const detailsY = yPosition + 10;
      doc.text(`Difficulty: ${data.difficulty.toUpperCase()}`, margin + 30, detailsY);
      doc.text(`Final Score: ${data.finalScore}`, margin + 30, detailsY + 12);
      
      const middleX = width / 2;
      doc.text(`Spiritual Bond: ${data.trustLevel}`, middleX, detailsY);
      doc.text(`Journey Date: ${new Date().toLocaleDateString()}`, middleX, detailsY + 12);

      yPosition += 40;

      // Add transcript title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(51, 51, 51);
      this.addCenteredText(doc, "Journey Transcript", yPosition);
      yPosition += 10;

      // Add messages
      doc.setFontSize(11);
      data.messages.forEach(msg => {
        if (yPosition > height - margin - 20) {
          doc.addPage();
          yPosition = margin + 10;
          this.drawDecorations(doc);
        }

        const contentWidth = width - 2 * (margin + 10);
        
        if (msg.type === 'user') {
          doc.setTextColor(0, 102, 204);
          doc.setFont("helvetica", "bold");
          const text = `[User] ${msg.content}`;
          yPosition += this.addWrappedText(doc, text, margin + 10, yPosition, contentWidth) + 3;
        } else if (msg.isFeedback) {
          doc.setTextColor(153, 51, 255);
          doc.setFont("helvetica", "italic");
          const text = `[Spirit Feedback] ${msg.content}`;
          yPosition += this.addWrappedText(doc, text, margin + 10, yPosition, contentWidth) + 3;
        } else {
          doc.setTextColor(51, 153, 102);
          doc.setFont("helvetica", "normal");
          const text = `[Spirit] ${msg.content}`;
          yPosition += this.addWrappedText(doc, text, margin + 10, yPosition, contentWidth) + 3;
        }
      });

      // Add footer
      const footerY = height - margin - 10;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("Generated by the Afterlife Guidance System", margin + 10, footerY);
      
      const timestamp = `Journey concluded: ${new Date().toLocaleString()}`;
      const timestampWidth = doc.getTextWidth(timestamp);
      doc.text(timestamp, width - margin - 10 - timestampWidth, footerY);

      // Save the PDF
      const fileName = `spiritual-journey-${data.gameName.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      doc.save(fileName);
    } catch (error: unknown) {
      console.error('PDF Generation Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error('Failed to generate PDF: ' + errorMessage);
    }
  }
} 